// Mock lesson data for study modes
export const grammarTopics = [
  {
    id: 'present_tense',
    name: '現在式',
    nameSimplified: '现在时',
    nameEnglish: 'Present Tense',
    difficulty: 'beginner',
    icon: '⏰',
    description: '學習現在式的基本用法和結構',
    descriptionSimplified: '学习现在时的基本用法和结构',
    descriptionEnglish: 'Learn the basic usage and structure of present tense'
  },
  {
    id: 'past_tense',
    name: '過去式',
    nameSimplified: '过去时',
    nameEnglish: 'Past Tense',
    difficulty: 'beginner',
    icon: '📅',
    description: '掌握過去式的變化規則',
    descriptionSimplified: '掌握过去时的变化规则',
    descriptionEnglish: 'Master the rules of past tense changes'
  },
  {
    id: 'future_tense',
    name: '未來式',
    nameSimplified: '将来时',
    nameEnglish: 'Future Tense',
    difficulty: 'beginner',
    icon: '🔮',
    description: '學習表達未來的各種方式',
    descriptionSimplified: '学习表达未来的各种方式',
    descriptionEnglish: 'Learn various ways to express the future'
  },
  {
    id: 'conditionals',
    name: '條件句',
    nameSimplified: '条件句',
    nameEnglish: 'Conditionals',
    difficulty: 'intermediate',
    icon: '🤔',
    description: '理解假設和條件的表達方式',
    descriptionSimplified: '理解假设和条件的表达方式',
    descriptionEnglish: 'Understand hypothetical and conditional expressions'
  },
  {
    id: 'passive_voice',
    name: '被動語態',
    nameSimplified: '被动语态',
    nameEnglish: 'Passive Voice',
    difficulty: 'intermediate',
    icon: '🔄',
    description: '學習被動語態的構成和使用',
    descriptionSimplified: '学习被动语态的构成和使用',
    descriptionEnglish: 'Learn the formation and use of passive voice'
  }
];

export const lessonContent = {
  present_tense: {
    readMode: {
      title: '現在式基礎',
      titleSimplified: '现在时基础',
      titleEnglish: 'Present Tense Basics',
      content: `
        現在式用來描述現在正在發生的動作或狀態。
        基本結構：主語 + 動詞原形/動詞第三人稱單數形式
        
        例句：
        • I work in an office. (我在辦公室工作)
        • She works in a hospital. (她在醫院工作)
        • They play football every weekend. (他們每週末踢足球)
      `,
      contentSimplified: `
        现在时用来描述现在正在发生的动作或状态。
        基本结构：主语 + 动词原形/动词第三人称单数形式
        
        例句：
        • I work in an office. (我在办公室工作)
        • She works in a hospital. (她在医院工作)
        • They play football every weekend. (他们每周末踢足球)
      `,
      contentEnglish: `
        Present tense is used to describe actions or states happening now.
        Basic structure: Subject + base verb/third person singular verb
        
        Examples:
        • I work in an office.
        • She works in a hospital.
        • They play football every weekend.
      `,
      image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400&h=300&fit=crop'
    },
    studyMode: {
      exercises: [
        {
          id: 'ex_001',
          type: 'fill_blank',
          question: 'I _____ (work) in a bank.',
          questionSimplified: 'I _____ (work) in a bank.',
          questionEnglish: 'I _____ (work) in a bank.',
          answer: 'work',
          options: ['work', 'works', 'working', 'worked'],
          explanation: '第一人稱單數使用動詞原形',
          explanationSimplified: '第一人称单数使用动词原形',
          explanationEnglish: 'First person singular uses base form of verb'
        },
        {
          id: 'ex_002',
          type: 'multiple_choice',
          question: 'She _____ English every day.',
          questionSimplified: 'She _____ English every day.',
          questionEnglish: 'She _____ English every day.',
          answer: 'studies',
          options: ['study', 'studies', 'studying', 'studied'],
          explanation: '第三人稱單數動詞要加s',
          explanationSimplified: '第三人称单数动词要加s',
          explanationEnglish: 'Third person singular verbs need -s ending'
        }
      ]
    },
    testMode: {
      questions: [
        {
          id: 'test_001',
          type: 'grammar_correction',
          question: 'He go to school every day.',
          questionSimplified: 'He go to school every day.',
          questionEnglish: 'He go to school every day.',
          answer: 'He goes to school every day.',
          points: 10
        },
        {
          id: 'test_002',
          type: 'translation',
          question: '她每天讀書',
          questionSimplified: '她每天读书',
          questionEnglish: 'Translate: She reads books every day.',
          answer: 'She reads books every day.',
          points: 15
        }
      ]
    }
  }
};

export const dailyChallenges = [
  {
    id: 'challenge_001',
    type: 'vocabulary',
    category: 'business',
    title: '商務詞彙挑戰',
    titleSimplified: '商务词汇挑战',
    titleEnglish: 'Business Vocabulary Challenge',
    description: '學習10個商務相關詞彙',
    descriptionSimplified: '学习10个商务相关词汇',
    descriptionEnglish: 'Learn 10 business-related vocabulary words',
    xp: 50,
    timeLimit: 300, // 5 minutes
    difficulty: 'intermediate'
  },
  {
    id: 'challenge_002',
    type: 'grammar',
    category: 'present_tense',
    title: '現在式練習',
    titleSimplified: '现在时练习',
    titleEnglish: 'Present Tense Practice',
    description: '完成15道現在式題目',
    descriptionSimplified: '完成15道现在时题目',
    descriptionEnglish: 'Complete 15 present tense questions',
    xp: 75,
    timeLimit: 600, // 10 minutes
    difficulty: 'beginner'
  },
  {
    id: 'challenge_003',
    type: 'speaking',
    category: 'daily',
    title: '日常對話練習',
    titleSimplified: '日常对话练习',
    titleEnglish: 'Daily Conversation Practice',
    description: '練習3分鐘日常對話',
    descriptionSimplified: '练习3分钟日常对话',
    descriptionEnglish: 'Practice 3-minute daily conversation',
    xp: 100,
    timeLimit: 180, // 3 minutes
    difficulty: 'beginner'
  }
];
